import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import parse from 'html-react-parser'
import { TerminalPaymentScene } from '@/models/payment-setting'
import 'dayjs/locale/ja'
import { CreateSpaceReservationFromSelfCI, fetchReservationsAndSales } from '@/apis/aipass'
import { NationalityType, OccupationType, isAccommodationInput, isEmployee } from '@/models/self-checkin/accommodation-info'
import { getTranslateOccupation } from '@/libs/self-checkin'
import { useGuestAppUrl } from '@/hooks/use-guest-app-url'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { SelfCheckInState } from '@/models/self-checkin'
import { SelectableTimeSelectionType } from '@/models/self-checkin/custom-checkin'
import dayjs from 'dayjs'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { groupBy } from 'lodash'
import { errorHandler } from '@/libs/errors'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { FooterAgreement } from '@/components/organisms/self-checkin/footer-agreement'
import { LoaderContextCreator } from '@/contexts/loader'
import { FrontTabletSmallCheckbox } from '@/components/molecules/front-tablet/input/small-checkbox'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'
import { FrontTabletFormLabel } from '@/components/molecules/front-tablet/input/form-label'
import { FrontTabletFormConfirmText } from '@/components/molecules/front-tablet/input/form-confirm-text'
import { reportError } from '@/components/apps/error-boundary'
import { Guide } from '@/utils/guide'
import { SelfCheckinContentCard } from '@/components/molecules/self-checkin-content-card'

export const Index: React.FC<{}> = () => {
  const { setIsLoading } = useContext(LoaderContextCreator())
  const [isDeliveryConfirm, setIsDeliveryConfirm] = useState<boolean>(true)
  const [isNeedPaymentSelect, setIsNeedPaymentSelect] = useState<boolean>(false)
  const [isFetchedServer, setIsFetchedServer] = useState<boolean>(false)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)

  const { state, saveState, doCheckIn, stateErrorHandling } = useSelfCheckInState()
  const { mypageUrl, homePageUrl } = useGuestAppUrl({
    hotelId: state.hotelId,
    plugin: state.setting?.plugin,
  })
  const [paymentInfo, setPaymentInfo] = useState<Required<SelfCheckInState>['confirm']['payment']>()
  const history = useHistory()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const pickByLanguage = usePickByLanguage()

  const isDeliveryConfirmed = useRef(state.selectReservation?.hasSmartCheckin || state.selectReservation?.hasAnyGuestCheckedIn)

  const accompanyData = () => {
    if (state.accommodationInfo) {
      return state.accommodationInfo.accompany.filter(accompany => accompany?.name.trim())
    }
    return []
  }

  const onClickCheckin = async () => {
    const nextState = {
      ...state,
      confirm: {
        isDeliveryConfirm: isDeliveryConfirmed.current ? undefined : isDeliveryConfirm,
        payment: paymentInfo,
      },
    }

    if (isNeedPaymentSelect) {
      saveState(nextState)
      history.push({ pathname: '/self-checkin/payment' })
      return
    }

    try {
      setIsLoading(true)
      await doCheckIn({
        state: nextState,
        language,
        guestAppUrl: { mypageUrl, homePageUrl },
      })
      history.push({ pathname: '/self-checkin/issue-room-key' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    } finally {
      setIsLoading(false)
    }
  }

  const goEditPage = (anchor: string) => {
    saveState({
      ...state,
      confirm: {
        isDeliveryConfirm: isDeliveryConfirmed.current ? undefined : isDeliveryConfirm,
        payment: paymentInfo,
      },
    })

    history.push({
      pathname: state.selectReservation?.hasAnyGuestCheckedIn ? '/self-checkin/additional-guest-info/edit' : '/self-checkin/info/edit',
      state: { anchor },
    })
  }

  const getTitle = (guide: Guide) => {
    if (language === 'ja') {
      return parse(guide.jpTitle)
    }
    if (language === 'en') {
      return parse(guide.enTitle)
    }
    if (language === 'ko') {
      return parse(guide.koTitle)
    }
    if (language === 'zh') {
      return parse(guide.zhTitle)
    }
  }
  const getTimeSelection = (timeSelectionId: string): SelectableTimeSelectionType => {
    return state.checkinCustomize?.timeSelections.find(({ selection }) => selection.id === timeSelectionId) as SelectableTimeSelectionType
  }

  const getTargetGuestName = (timeSelectionId: string, date: string): string => {
    if ((state.selectReservation?.selectedReservations.length || 0) < 2) {
      return ''
    }
    return getTimeSelection(timeSelectionId)
      .reservationsByTime[date].map(reserveId => {
        const reserve = state.selectReservation!.selectedReservations.find(r => r.reservationId === reserveId)
        return t('honorific', { name: reserve?.guestName || reserve?.userName || '-' })
      })
      .join(', ')
  }

  const initializeComponent = async () => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }
    if (state.complete?.hotelId) {
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
      return
    }
    setIsInitializeComponent(true)
    if (!state.selectReservation) {
      setIsFetchedServer(true)
      return
    }
    setIsLoading(true)

    if (state.checkinCustomize?.assignSpaces.length) {
      const groupedAssignSpaces = groupBy(state.checkinCustomize.assignSpaces, 'reservationId')
      const groupReservationMap = Object.keys(groupedAssignSpaces)
      try {
        for (let i = 0; i < groupReservationMap.length; i++) {
          await CreateSpaceReservationFromSelfCI({
            paymentMethodType: 'checkout',
            hotelId: state.hotelId,
            assignSpaces: groupedAssignSpaces[groupReservationMap[i]],
          })
        }
      } catch (e) {
        setIsLoading(false)
        errorHandler(e)
        goEditPage('guidance-block')
        return
      }
    }

    fetchReservationsAndSales({
      hotelId: state.hotelId,
      reservationIds: state.selectReservation.selectedReservationIds,
      filterGuestRoomAssignIds: state.selectReservation.selectedReservationAndRooms.flatMap(r => r.guestRoomAssignIds),
    })
      .then(res => {
        const accommodationInput = isAccommodationInput(state.accommodationInfo) ? state.accommodationInfo : undefined
        const suggestReceiptName =
          accommodationInput?.company || `${state.accommodationInfo?.lastName} ${state.accommodationInfo?.firstName}`
        // 手入力由来の宛名は維持、それ以外は会社名 > 宿泊者名で毎回再計算する
        setPaymentInfo({
          receiptName: state.confirm?.payment?.isReceiptNameManual ? state.confirm.payment.receiptName : suggestReceiptName,
          isReceiptNameManual: Boolean(state.confirm?.payment?.isReceiptNameManual),
          usage: res,
        })

        const isConnectedTerminal = state.setting.isEnabledPaymentTerminal || state.setting.isEnabledCashChanger
        const terminalPaymentScene = state.setting.paymentTerminal.terminalPaymentScene ?? TerminalPaymentScene.Both

        // チェックイン時に支払い可能か（端末あり+CheckoutOnly以外 or スマート決済あり）
        const canPayAtCheckin =
          (isConnectedTerminal && terminalPaymentScene !== TerminalPaymentScene.CheckoutOnly) ||
          (!isConnectedTerminal && state.setting.payment.length > 0)

        setIsNeedPaymentSelect(canPayAtCheckin && res.totalAmount > 0)
      })
      .finally(() => {
        setIsLoading(false)
        setIsFetchedServer(true)
      })
  }

  useEffect(() => {
    initializeComponent()
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }

  const accompanyList = accompanyData()
  const showDeliveryConsent = Boolean(state.selectReservation && !isDeliveryConfirmed.current)

  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: history.goBack, title: t('Please confirm the contents') }}
      footer={{
        right: {
          isDisabled: !isFetchedServer,
          label: t(isNeedPaymentSelect ? 'Next' : 'Submit'),
          onClick: onClickCheckin,
        },
      }}
    >
      <SelfCheckinContentCard>
        <div css={descriptionStyle}>
          <p>{t('Representative information')}</p>
          <div css={editButtonStyle} onClick={() => goEditPage('accommodation-block')}>
            <img src={require('@/static/images/edit.svg')} />
          </div>
        </div>
        <div css={itemWrapperStyle}>
          {state.uploadLicense?.identificationFrontImage && (
            <>
              <FrontTabletFormLabel label={t('Identification')} required={true} />
              <div css={identificationWrapperStyle}>
                <img src={state.uploadLicense.identificationFrontImage} />
                <img src={state.uploadLicense.identificationBackImage} />
              </div>
            </>
          )}
          {state.uploadLicense?.representativePassportImage && (
            <>
              <FrontTabletFormLabel label={t('Identification')} required={true} />
              <div css={identificationWrapperStyle}>
                <img src={state.uploadLicense.representativePassportImage} />
              </div>
            </>
          )}
        </div>
        <div css={personalInfoGridStyle}>
          {/* 2行目: 居住国（全幅） */}
          <div css={[itemWrapperStyle, residenceFullWidthStyle]}>
            <FrontTabletFormLabel label={t('Residence')} required={true} />
            <div css={itemTextStyle}>
              <FrontTabletFormConfirmText
                text={state.accommodationInfo?.nationality === NationalityType.Jp ? t('Japanese nationality') : t('Foreign nationality')}
              />
            </div>
          </div>
          {/* 3行目: 氏名（左）・フリガナ（右） */}
          <div css={itemWrapperStyle}>
            <FrontTabletFormLabel label={t('Full name')} required={true} />
            <div css={itemTextStyle}>
              <FrontTabletFormConfirmText text={`${state.accommodationInfo?.lastName} ${state.accommodationInfo?.firstName}`} />
            </div>
          </div>

          {isAccommodationInput(state.accommodationInfo) && (
            <>
              {state.accommodationInfo?.nationality === NationalityType.Jp && language === 'ja' && (
                <div css={itemWrapperStyle}>
                  <FrontTabletFormLabel label={t('Furigana')} required={!!state.setting.accommodation.requiredFuriganaInput} />
                  <div css={itemTextStyle}>
                    <FrontTabletFormConfirmText
                      text={`${state.accommodationInfo?.lastNameFurigana} ${state.accommodationInfo?.firstNameFurigana}`}
                    />
                  </div>
                </div>
              )}
              {/* 4行目: 生年月日（左）・性別（右） */}
              <div css={itemWrapperStyle}>
                <FrontTabletFormLabel label={t('Date of birth')} required={!!state.setting.accommodation.requiredBirthDateInput} />
                <div css={itemTextStyle}>
                  <FrontTabletFormConfirmText text={state.accommodationInfo?.dateOfBirth || ''} />
                </div>
              </div>
              <div css={itemWrapperStyle}>
                <FrontTabletFormLabel label={t('Gender')} required={!!state.setting.accommodation.requiredGenderInput} />
                <div css={itemTextStyle}>
                  <FrontTabletFormConfirmText text={state.accommodationInfo?.gender === 'F' ? t('Female') : t('Male')} />
                </div>
              </div>
              {/* 5行目: 電話番号（左）・メールアドレス（右） */}
              <div css={itemWrapperStyle}>
                <FrontTabletFormLabel label={t('Phone number')} required={!!state.setting.accommodation.requiredTelephoneInput} />
                <div css={itemTextStyle}>
                  <FrontTabletFormConfirmText text={state.accommodationInfo?.phoneNumber || '-'} />
                </div>
              </div>
              <div css={itemWrapperStyle}>
                <FrontTabletFormLabel label={t('Email')} required={!!state.setting.accommodation.requiredEmailInput} />
                <div css={itemTextStyle}>
                  <FrontTabletFormConfirmText text={state.accommodationInfo?.mail || '-'} />
                </div>
              </div>
              {/* 6行目: 職業（左）・お勤め先（右） */}
              {state.accommodationInfo?.occupation !== OccupationType.Other && (
                <div css={itemWrapperStyle}>
                  <FrontTabletFormLabel label={t('Occupation')} required={false} />
                  <div css={itemTextStyle}>
                    <FrontTabletFormConfirmText
                      text={state.accommodationInfo?.occupation ? t(`${getTranslateOccupation(state.accommodationInfo?.occupation)}`) : '-'}
                    />
                  </div>
                </div>
              )}
              {state.accommodationInfo?.occupation === OccupationType.Other && (
                <div css={itemWrapperStyle}>
                  <FrontTabletFormLabel label={t('Occupation')} required={false} />
                  <div css={itemTextStyle}>
                    <FrontTabletFormConfirmText text={state.accommodationInfo?.otherOccupation || '-'} />
                  </div>
                </div>
              )}
              {isEmployee(state.accommodationInfo?.occupation) && (
                <div css={itemWrapperStyle}>
                  <FrontTabletFormLabel label={t('Company')} required={false} />
                  <div css={itemTextStyle}>
                    <FrontTabletFormConfirmText text={state.accommodationInfo?.company || '-'} />
                  </div>
                </div>
              )}
              {/* 7行目: 郵便番号（左）・住所（右） */}
              {state.accommodationInfo?.nationality === 'japanese' && (
                <div css={itemWrapperStyle}>
                  <FrontTabletFormLabel label={t('Post code')} required={false} />
                  <div css={itemTextStyle}>
                    <FrontTabletFormConfirmText text={state.accommodationInfo?.postCode || '-'} />
                  </div>
                </div>
              )}
              <div css={itemWrapperStyle}>
                <FrontTabletFormLabel label={t('Address')} required={!!state.setting.accommodation.requiredAddressInput} />
                <div css={itemTextStyle}>
                  <FrontTabletFormConfirmText text={state.accommodationInfo?.address || '-'} />
                </div>
              </div>
            </>
          )}

          {/* 8行目: 宿泊人数（左）・同行者名（右） */}
          <div css={itemWrapperStyle}>
            <FrontTabletFormLabel label={t('Number of guests')} required={!!state.setting.accommodation.requiredAccompanyNameInput} />
            <div css={itemTextStyle}>
              <FrontTabletFormConfirmText
                text={`${state.accommodationInfo?.paxTotal ?? 0} ${t((state.accommodationInfo?.paxTotal ?? 0) > 1 ? 'Persons' : 'Person')}`}
              />
            </div>
          </div>
          {!!accompanyList.length && (
            <div css={itemWrapperStyle}>
              <FrontTabletFormLabel label={t('Accompany name')} required={!!state.setting.accommodation.requiredAccompanyNameInput} />
              <div css={itemTextStyle}>
                <FrontTabletFormConfirmText
                  text={accompanyList.map(accompany => `${accompany?.lastName} ${accompany?.firstName}`).join('／')}
                />
              </div>
            </div>
          )}
        </div>
      </SelfCheckinContentCard>

      {Boolean(
        state.checkinCustomize?.guides.filter(g => g.inputType !== 'none').length || state.checkinCustomize?.timeSelections.length,
      ) && (
        <SelfCheckinContentCard>
          <div css={descriptionStyle}>
            <p>{t('Guidance')}</p>
            <div css={editButtonStyle} onClick={() => goEditPage('guidance-block')}>
              <img src={require('@/static/images/edit.svg')} />
            </div>
          </div>
          {state.checkinCustomize?.guides
            .filter(g => g.inputType !== 'none')
            .map(guide => (
              <div key={guide.customCheckinId} css={itemWrapperStyle}>
                <FrontTabletFormLabel label={getTitle(guide)} required={Boolean(guide.isRequired)} />
                <div css={itemTextStyle}>
                  <FrontTabletFormConfirmText text={guide.selectedAnswer || '-'} />
                </div>
              </div>
            ))}

          {Object.keys(state.checkinCustomize?.selectedTime || {}).map((selectionId, i) => {
            const { selection } = getTimeSelection(selectionId)
            return (
              <div key={selectionId} css={itemWrapperStyle}>
                <FrontTabletFormLabel label={pickByLanguage(selection.title)} required={Boolean(selection.isRequired)} />
                {state.checkinCustomize?.selectedTime[selectionId].length ? (
                  state.checkinCustomize?.selectedTime[selectionId]
                    .sort((a, b) => (dayjs(a.day).isAfter(dayjs(b.day)) ? 1 : -1))
                    .filter(({ time }) => time)
                    .map(selectedDate => {
                      const day = dayjs(selectedDate.day).format(t('MM-DD(ddd)'))
                      const guestName = getTargetGuestName(selectionId, selectedDate.day)
                      return (
                        <div css={itemTextStyle} key={`${selectionId}-${selectedDate.day}`}>
                          <FrontTabletFormConfirmText
                            key={`${selectionId}-${selectedDate.day}`}
                            text={`${day} ${selectedDate.time} ${guestName}`}
                          />
                        </div>
                      )
                    })
                ) : (
                  <div css={itemTextStyle}>
                    <FrontTabletFormConfirmText text="-" />
                  </div>
                )}
              </div>
            )
          })}
        </SelfCheckinContentCard>
      )}

      {isNeedPaymentSelect && paymentInfo && (
        <SelfCheckinContentCard>
          <div css={descriptionStyle}>
            <p>{t('Payment details')}</p>
            <div css={editButtonStyle} onClick={() => goEditPage('payment-detail-block')}>
              <img src={require('@/static/images/edit.svg')} />
            </div>
          </div>
          <div css={itemWrapperStyle}>
            <FrontTabletFormLabel label={t('Amount billed')} required={true} />
            <div css={itemTextStyle}>
              <FrontTabletFormConfirmText text={`¥${paymentInfo.usage.totalAmount.toLocaleString()}`} />
            </div>
          </div>
          <div css={itemWrapperStyle}>
            <FrontTabletFormLabel label={t('Name of recipient')} />
            <div css={itemTextStyle}>
              <FrontTabletFormConfirmText text={paymentInfo.receiptName} />
            </div>
          </div>
        </SelfCheckinContentCard>
      )}

      {showDeliveryConsent && (
        <div css={marketingConsentStyle}>
          <FrontTabletSmallCheckbox
            onChange={() => setIsDeliveryConfirm(!isDeliveryConfirm)}
            label={t('Receive campaign information and special offers by e-mail')}
            value={isDeliveryConfirm}
          />
        </div>
      )}

      <div css={[agreementWrapperStyle, showDeliveryConsent && agreementAfterConsentStyle]}>
        <FooterAgreement />
      </div>
    </SelfCheckinLayout>
  )
}

const personalInfoGridStyle = css(
  {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 30,
    rowGap: 24,
    gridAutoRows: 66,
    '> div': {
      paddingBottom: 0,
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { columnGap: 40, rowGap: 32, gridAutoRows: 'auto' },
  }),
)

const residenceFullWidthStyle = css({
  gridColumn: '1 / -1',
})

const descriptionStyle = css(
  {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    paddingBottom: 27,
    p: {
      fontSize: 24,
      lineHeight: '30px',
      letterSpacing: '1.2px',
      textAlign: 'center',
      fontWeight: 'bold',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { paddingBottom: 35, p: { fontSize: 28, lineHeight: '35px', letterSpacing: '1.4px' } },
  }),
)

const editButtonStyle = css({
  cursor: 'pointer',
  position: 'absolute',
  right: 0,
  width: 40,
  height: 40,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  backgroundColor: '#F2F2F2',
  img: {
    width: 30,
  },
})

const itemWrapperStyle = css({
  width: '100%',
  padding: '0 0 24px',
  '&:last-child': {
    padding: 0,
  },
})

const itemTextStyle = css({
  marginTop: 8,
})

const identificationWrapperStyle = css(
  {
    marginTop: 6,
    display: 'flex',
    justifyContent: 'flex-start',
    img: {
      width: 282,
      height: 198,
      borderRadius: 10,
      '&:nth-of-type(1)': {
        margin: '0 24px 0 0',
      },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { flexWrap: 'wrap', gap: 20, img: { '&:nth-of-type(1)': { margin: 0 } } },
    landscape13: { marginTop: 9 },
  }),
)

const marketingConsentStyle = css(
  {
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    alignItems: 'center',
    marginTop: 8,
    p: {
      fontSize: 18,
      fontWeight: 500,
      letterSpacing: '0.9px',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { p: { fontSize: 22, letterSpacing: '1.1px' } },
  }),
)

const agreementWrapperStyle = css({
  '> div': { marginBottom: 0 },
})

const agreementAfterConsentStyle = css({
  '> div': { marginTop: 0 },
})
